/**
 * The library's row list — grouping, ordering and the name filter.
 *
 * Pure, because the interesting part is the *shape* of the list and `npm test` has no DOM.
 *
 * **One level of folders, deliberately.** A folder is a work (サクラノ詩); a log's whole address is
 * `folder + name`, which is what makes *Move to folder* a single pick rather than a tree walk and
 * keeps the row flat enough to drag. Routes and arcs belong in the log name (`04 · 横浜基地`), where
 * they sort naturally.
 */

import type { ReadingFolder, ReadingLog } from "@/lib/reading";
import { unsavedLabel } from "./logTabs";

/**
 * The synthetic folder that holds unnamed logs.
 *
 * Not a row in the database — `Unfiled` is "these logs have no name", not a place you can move
 * something into. Listing them at all is the point: a scratchpad you have forgotten about is
 * exactly the thing that eats a month of captures, so they are listed rather than hidden.
 */
export const UNFILED_ID = "__unfiled__";

/**
 * What that row is CALLED, kept apart from the id it is keyed by.
 *
 * They were one string until the Japanese pass, and that is exactly the collision that makes a
 * label untouchable: `Library.tsx` compares `row.id === UNFILED_ID` in seven places to decide the
 * row has no menu, no rename and no drop target. So the label moved out and the id did not move at
 * all — translating a key is how a synthetic row silently becomes a real one.
 */
export const UNFILED_NAME = "未整理";

export type LibraryRow =
  | { kind: "folder"; id: string; name: string; logCount: number; sentenceCount: number }
  | { kind: "log"; log: ReadingLog; label: string; folderName: string | null; indented: boolean };

/**
 * Folders, then root logs, then `Unfiled` — with each folder's logs beneath it.
 *
 * **A named log with no folder sits at the ROOT, un-indented, not under `Unfiled`.** That falls
 * straight out of "naming it is the only thing that files it": the moment a log has a name it has
 * left the scratchpad drawer, whether or not you have decided which work it belongs to.
 *
 * **`Unfiled` is therefore *unnamed AND folderless*, not merely unnamed** — the `folderId === null`
 * half is load-bearing rather than a tightening. `Move to folder` is offered on every log row,
 * unnamed ones included, so an unnamed log can genuinely have a folder; without that clause it
 * matched both this group and its folder's and the row was emitted **twice**, under one React key.
 * The folder wins because the server already says so: `library()` computes a folder's
 * `logCount`/`sentenceCount` from `folder_id` alone, so listing the log anywhere else would leave
 * the folder row disagreeing with the rows beneath it.
 *
 * `Unfiled` goes last and is omitted entirely when there is nothing in it — an empty drawer is a
 * row that never says anything.
 *
 * @param collapsed Folder ids whose logs are hidden. Folders themselves always render.
 * @param query Matched against log names and folder names, case-insensitively. **Names only** —
 *   sentence search over 6,000+ rows is a different query and is deliberately not this one.
 */
export function libraryRows(
  folders: readonly ReadingFolder[],
  logs: readonly ReadingLog[],
  { collapsed = new Set<string>(), query = "" }: { collapsed?: ReadonlySet<string>; query?: string } = {},
): LibraryRow[] {
  const needle = query.trim().toLowerCase();
  const label = (entry: ReadingLog) => entry.name ?? unsavedLabel(entry);
  const matches = (entry: ReadingLog, folderName: string | null) =>
    needle === "" ||
    label(entry).toLowerCase().includes(needle) ||
    (folderName ?? "").toLowerCase().includes(needle);

  const byOrder = <T extends { sortOrder: number }>(a: T, b: T) => a.sortOrder - b.sortOrder;
  const rows: LibraryRow[] = [];

  for (const folder of [...folders].sort(byOrder)) {
    const inside = logs.filter((entry) => entry.folderId === folder.id).sort(byOrder);
    const visible = inside.filter((entry) => matches(entry, folder.name));
    // A folder whose name matches keeps ALL its logs, so searching for a work shows the work.
    // A folder that survives only because one log inside it matched shows just that log.
    const folderMatches = needle !== "" && folder.name.toLowerCase().includes(needle);
    const shown = folderMatches ? inside : visible;
    if (needle !== "" && shown.length === 0 && !folderMatches) continue;

    rows.push({
      kind: "folder",
      id: folder.id,
      name: folder.name,
      // The folder's OWN totals, not the filtered subset's. A search narrows what you can see, and
      // making the count follow it would mean the row disagrees with itself the moment you type.
      logCount: folder.logCount,
      sentenceCount: folder.sentenceCount,
    });
    if (collapsed.has(folder.id)) continue;
    for (const entry of shown) {
      rows.push({
        kind: "log",
        log: entry,
        label: label(entry),
        folderName: folder.name,
        indented: true,
      });
    }
  }

  // Named, no folder: the root. Un-indented, because there is no folder row above them to indent
  // under — the indent is what says "inside that".
  for (const entry of logs
    .filter((candidate) => candidate.name !== null && candidate.folderId === null)
    .sort(byOrder)) {
    if (!matches(entry, null)) continue;
    rows.push({ kind: "log", log: entry, label: label(entry), folderName: null, indented: false });
  }

  const unfiled = logs
    .filter((entry) => entry.name === null && entry.folderId === null)
    .sort(byOrder);
  const shownUnfiled = unfiled.filter((entry) => matches(entry, UNFILED_NAME));
  if (unfiled.length > 0 && shownUnfiled.length > 0) {
    rows.push({
      kind: "folder",
      id: UNFILED_ID,
      name: UNFILED_NAME,
      logCount: unfiled.length,
      sentenceCount: unfiled.reduce((total, entry) => total + entry.sentenceCount, 0),
    });
    if (!collapsed.has(UNFILED_ID)) {
      for (const entry of shownUnfiled) {
        rows.push({
          kind: "log",
          log: entry,
          label: label(entry),
          folderName: null,
          indented: true,
        });
      }
    }
  }

  return rows;
}

/** The header's `フォルダ4 · ログ31 · 6,412文`. */
export function librarySummary(
  folders: readonly ReadingFolder[],
  logs: readonly ReadingLog[],
): string {
  const sentences = logs.reduce((total, entry) => total + entry.sentenceCount, 0);
  return [
    `フォルダ${folders.length}`,
    `ログ${logs.length}`,
    `${sentences.toLocaleString()}文`,
  ].join(" · ");
}

/**
 * What a delete is about to take, for the confirmation.
 *
 * Stated rather than implied — `283文、作成カード61枚` is the difference between a confirmation you
 * read and one you dismiss. The mined count is why `minedNoteIds` holds real ids rather than a
 * counter.
 */
export function deleteWarning(entry: ReadingLog): string {
  const parts = [`${entry.sentenceCount}文`];
  if (entry.minedCount > 0) {
    parts.push(`作成カード${entry.minedCount}枚`);
  }
  return parts.join("、");
}
